import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import ConfigurableOption from '~/components/core/Molecules/ConfigurableOption';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import AuthorityLevelsSection from '~/components/SystemConfiguration/DelegatedAuthority/AuthorityLevelsSection';
import OverrideRulesSection from '~/components/SystemConfiguration/DelegatedAuthority/OverrideRules/OverrideRulesSection';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import Switch from '~/components/ui/atoms/Switch';
import useDataFetcher from '~/components/useDataFetcher';
import type { DAConfigInfo, UpdateDAConfigParams } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface SubTabProps {
  /** The configured organization. Only the override-rules sub-tab reads it, for the rules table's
   *  sub-organizations column, but `InnerTabs` types every tab's props alike. */
  organization: SysconfigOrganization;
  organizationId: number;
  arePaymentLimitsSplit: boolean;
}

const DelegatedAuthorityTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const configRoute = `/sysconfig/api/v1/organizations/${organization.id}/delegated_authority/`;

  const { isLoading, isError, data, reloadData } = useDataFetcher<DAConfigInfo>(configRoute);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggle = async (enabled: boolean) => {
    try {
      setIsUpdating(true);
      const payload: UpdateDAConfigParams = { enabled };
      await axios.patch(configRoute, payload);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleRoundRobin = async (_event: unknown, roundRobinEnabled: boolean) => {
    if (!data) {
      return;
    }
    try {
      setIsUpdating(true);
      const payload: UpdateDAConfigParams = {
        enabled: data.enabled,
        round_robin_approver_selection_enabled: roundRobinEnabled,
      };
      await axios.patch(configRoute, payload);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const subTabs = data
    ? [
        {
          label: TEXTS.SUBTAB_LEVELS,
          url: 'levels',
          component: AuthorityLevelsSection,
          props: {
            organization,
            organizationId: organization.id,
            arePaymentLimitsSplit: data.are_payment_limits_split,
          } as SubTabProps,
        },
        {
          label: TEXTS.SUBTAB_OVERRIDE_RULES,
          url: 'override-rules',
          component: OverrideRulesSection,
          props: {
            organization,
            organizationId: organization.id,
            arePaymentLimitsSplit: data.are_payment_limits_split,
          } as SubTabProps,
        },
      ]
    : [];

  return (
    <div className="bg-slate-100 pt-32">
      <CardDialog width="md" noCardTitle>
        <Heading variant={Heading.TYPES.H1}>{TEXTS.TITLE}</Heading>
        <div style={{ marginTop: 8 }}>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.SUBTITLE}
          </Text>
        </div>
        <LoadingSwitch isError={isError} isLoading={isLoading}>
          <ConfigurableOption<DAConfigInfo>
            isEnabled={data?.enabled}
            onChangeToggle={handleToggle}
            configurationView={<Heading variant={Heading.TYPES.H3}>{TEXTS.ENABLE_TOGGLE_LABEL}</Heading>}
            configuration={data}
            showOnly={isUpdating}
          />
          {data?.enabled && (
            <div className="mt-12 flex items-start gap-12">
              <Switch
                checked={data.round_robin_approver_selection_enabled ?? true}
                onChange={handleToggleRoundRobin}
                disabled={isUpdating}
                aria-label={TEXTS.ROUND_ROBIN_TOGGLE_LABEL}
              />
              <div className="flex flex-col">
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                  {TEXTS.ROUND_ROBIN_TOGGLE_LABEL}
                </Text>
                <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                  {TEXTS.ROUND_ROBIN_TOGGLE_HELP}
                </Text>
              </div>
            </div>
          )}
          {data && <InnerTabs<SubTabProps> tabs={subTabs} />}
        </LoadingSwitch>
      </CardDialog>
    </div>
  );
};

export default DelegatedAuthorityTab;
